import { useCallback, useMemo } from "react";

import { useRouter } from "next/router";

import type { ParsedUrlQuery } from "querystring";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import { SEARCH_ROUTE_PARAM } from "@/features/search/api/route";
import {
  patchQuery,
  rangeKeys,
  toggleTermValue,
  urlQueryFromParams,
} from "@/features/search/utils/search-params.utils";

type QueryPatch = Record<string, string | string[] | number | null | undefined>;

/**
 * Query keys that survive "clear all": the catch-all segment (which landing page you
 * are on) and `query` (what you searched for) identify the page — they are not filters.
 * The segment is also what Next.js interpolates back into `/[...search]`, so dropping
 * it would make the route un-buildable.
 */
const IDENTITY_KEYS = [SEARCH_ROUTE_PARAM, "query"];

const identityOnly = (query: ParsedUrlQuery): ParsedUrlQuery =>
  Object.fromEntries(
    IDENTITY_KEYS.flatMap(key => (query[key] ? [[key, query[key]]] : []))
  );

/**
 * All URL mutations for search live here, so components never touch the router
 * or build query strings themselves. Filter changes route shallowly (no server
 * round-trip — the client-side query refetches); they also reset `page`.
 */
export const useSearchNavigation = () => {
  const router = useRouter();
  const { pathname, query } = router;

  /** Href string for the current page with `patch` applied (for crawlable links). */
  const buildHref = useCallback(
    (patch: QueryPatch) => ({ pathname, query: patchQuery(query, patch) }),
    [pathname, query]
  );

  const apply = useCallback(
    (patch: QueryPatch, { shallow = true } = {}) =>
      router.push({ pathname, query: patchQuery(query, patch) }, undefined, {
        shallow,
        scroll: false,
      }),
    [router, pathname, query]
  );

  /** Filter change: apply patch AND reset to page 1. */
  const applyFilter = useCallback(
    (patch: QueryPatch) => apply({ ...patch, page: null }),
    [apply]
  );

  const actions = useMemo(
    () => ({
      buildHref,
      setPage: (page: number) => apply({ page: page <= 1 ? null : page }),
      toggleTerm: (key: string, value: string) =>
        applyFilter({ [key]: toggleTermValue(query[key], value) }),
      clearTerm: (key: string) => applyFilter({ [key]: null }),
      /*
       * One param, one value — what a choice facet applies. The service names the param, so this
       * stays a dumb setter rather than knowing which filter it is driving.
       */
      setParam: (param: string, value: string | null) =>
        applyFilter({ [param]: value }),
      setRange: (key: string, min?: number | null, max?: number | null) => {
        const { min: minKey, max: maxKey } = rangeKeys(key);
        applyFilter({ [minKey]: min ?? null, [maxKey]: max ?? null });
      },
      setSort: (sort: string | null, order?: string | null) =>
        applyFilter({ sort, order }),
      setQuery: (searchTerm: string | null) =>
        applyFilter({ query: searchTerm }),
      /**
       * Take the canonical query string the service reports and make it the URL.
       *
       * The one thing a client has to understand about natural language: a search whose filters were
       * read out of a sentence has them nowhere the URL can express, so a chip built from one cannot
       * be cleared. `meta.rules.params` is that same search written in params — adopting it turns the
       * lot into ordinary filters, after which chips, removal, sharing and saving all work with no
       * further knowledge of how they got there.
       *
       * `replace`, not `push`: the sentence and its params are one search, so a back button that
       * returned to the sentence would re-run the model and land right back here.
       */
      adopt: (params: string) => {
        const segment = query[SEARCH_ROUTE_PARAM];
        const next: ParsedUrlQuery = {
          ...(segment ? { [SEARCH_ROUTE_PARAM]: segment } : {}),
          ...urlQueryFromParams(params),
        };

        return router.replace({ pathname, query: next }, undefined, {
          shallow: true,
          scroll: false,
        });
      },
      reset: () => {
        /*
         * The one analytics call in this layer, and it earns the exception: three buttons clear
         * the filters — the chip row, the mobile panel footer and the empty state — and they all
         * mean the same thing, so reporting here covers them at once rather than leaving three
         * places to forget.
         */
        analyticsService.trackFilterResetList();

        return router.push(
          { pathname, query: identityOnly(query) },
          undefined,
          { scroll: false }
        );
      },
    }),
    [buildHref, apply, applyFilter, query, router, pathname]
  );

  return actions;
};

export type SearchNavigation = ReturnType<typeof useSearchNavigation>;
